import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { ReactElement, useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useSearchParams } from 'react-router-dom';
import KinnectPasswordResetPageStore from '../stores/KinnectPasswordResetPageStore';
import KinnectLandingPage from './KinnectLandingPage';
import KinnectPasswordValidationText from './KinnectPasswordValidationText';
import { observer } from 'mobx-react';
import CloseIcon from '@mui/icons-material/Close';
import { RequestStatus } from '../../../shared/enums/RequestStatus';

const KinnectPasswordResetPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const encodedToken = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const [store] = useState(() => new KinnectPasswordResetPageStore());

  const handleSubmit = () => {
    if (encodedToken) {
      void store.resetPassword(encodedToken);
    }
  };

  return (
    <KinnectLandingPage
      sx={{ backgroundColor: '#F2F2F4' }}
      headerSx={{ marginTop: 6, marginBottom: 7 }}
    >
      {store.requestStatus === RequestStatus.SUCCESS && (
        <Typography
          sx={{
            fontFamily: 'EdmondSansMedium',
            color: 'green',
            paddingX: 5,
            paddingTop: 3.5,
          }}
        >
          Your password was successfully reset! You can now close this page.
        </Typography>
      )}
      {store.requestStatus === RequestStatus.FAILURE && (
        <Typography
          sx={{
            fontFamily: 'EdmondSansMedium',
            color: 'red',
            paddingX: 5,
            paddingTop: 3.5,
          }}
        >
          Password reset was unsuccessful. Please refresh the page and try again
          or contact support at kinnectnow@gmail.com.
        </Typography>
      )}
      {store.requestStatus === RequestStatus.INITIAL && (
        <>
          <Stack
            spacing={2}
            sx={{
              width: {
                xs: '90%',
                sm: '80%',
                md: '50%',
              },
              marginLeft: 'auto',
              marginRight: 'auto',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'EdmondsansMedium',
                fontSize: 20,
                paddingBottom: 2,
              }}
            >
              Reset Your Password
            </Typography>
            <FormControl
              variant="outlined"
              sx={{ width: { xs: '85%', md: '100%' } }}
            >
              <InputLabel
                htmlFor="password-input"
                sx={{ fontFamily: 'EdmondsansMedium' }}
              >
                New Password
              </InputLabel>
              <OutlinedInput
                id="password-input"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword
                          ? 'Hide the password'
                          : 'Display the password'
                      }
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                onChange={(e) => {
                  store.setNewPassword(e.target.value);
                  store.validatePassword(e.target.value);
                }}
                sx={{
                  fontFamily: 'EdmondsansMedium',
                  borderRadius: 2,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: '#6195C4',
                    },
                }}
              />
            </FormControl>
            <FormControl
              variant="outlined"
              sx={{ width: { xs: '85%', md: '100%' } }}
            >
              <InputLabel
                htmlFor="password-confirmation-input"
                sx={{ fontFamily: 'EdmondsansMedium', borderRadius: 2 }}
              >
                Confirm New Password
              </InputLabel>
              <OutlinedInput
                id="password-confirmation-input"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword
                          ? 'Hide the password'
                          : 'Display the password'
                      }
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                onChange={(e) => {
                  store.setNewPasswordConfirmation(e.target.value);
                }}
                sx={{
                  fontFamily: 'EdmondsansMedium',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    width: 150,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: '#6195C4',
                    },
                }}
              />
            </FormControl>

            <Box
              sx={{
                flexDirection: 'column',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                paddingTop: 0.75,
              }}
            >
              <Box sx={{ width: 300, marginLeft: 7.5 }}>
                <KinnectPasswordValidationText
                  isValid={store.passwordValidation.isAtLeastFiveChars}
                  text={'Must be at least 5 characters in length'}
                />
                <KinnectPasswordValidationText
                  isValid={store.passwordValidation.hasUppercase}
                  text={'Must contain an uppercase letter'}
                />
                <KinnectPasswordValidationText
                  isValid={store.passwordValidation.hasLowercase}
                  text={'Must contain a lowercase letter'}
                />
                <KinnectPasswordValidationText
                  isValid={store.passwordValidation.hasDigit}
                  text={'Must contain a numerical digit'}
                />

                {store.errors.map((error, index) => {
                  return (
                    <Box
                      key={index}
                      sx={{ display: 'flex', flexDirection: 'row' }}
                    >
                      <CloseIcon
                        sx={{
                          height: 13,
                          width: 13,
                          paddingTop: 0.15,
                          paddingLeft: 1,
                          color: 'red',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'EdmondSansMedium',
                          color: 'red',
                          fontSize: 12,
                          paddingLeft: 1,
                          paddingRight: 6.5,
                          textAlign: 'left',
                          width: '100%',
                        }}
                      >
                        {error}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Stack>

          <Button
            variant={'contained'}
            onClick={handleSubmit}
            sx={{
              fontFamily: 'EdmondsansMedium',
              width: 200,
              height: 47.5,
              marginLeft: 'auto',
              marginRight: 'auto',
              borderRadius: 2,
              textTransform: 'none',
              marginTop: 3,
              paddingTop: 1.15,
              backgroundColor: '#6195C4',
            }}
          >
            Submit
          </Button>
        </>
      )}
    </KinnectLandingPage>
  );
};

export default observer(KinnectPasswordResetPage);
